// Reunderwriting Module (Tier 3) PR 4 — escalation detail drawer with
// approve/deny flow. Pattern-matched on the dealflow EscalationDetailDrawer.

import { useEffect, useState } from "react";
import { Button } from "@/design-system/components/ui";
import type { ReunderwritingEscalationDetailDto } from "@/types/reunderwriting";
import { useDateFormatter } from "@/utils/datetime";

const DIRECTIVE_PRESETS = [
  "Proceed with Waiver",
  "Decline this Item",
  "Require Additional Documentation",
  "Approve as Pass",
];

interface Props {
  open: boolean;
  detail: ReunderwritingEscalationDetailDto | null;
  isLoading: boolean;
  canResolve: boolean;
  onClose: () => void;
  onApprove: (body: { resolutionNotes: string; resolutionDirective: string }) => Promise<unknown>;
  onDeny: (body: { resolutionNotes: string; resolutionDirective: string }) => Promise<unknown>;
  isSubmitting: boolean;
}

export function ReunderwritingEscalationDetailDrawer({
  open,
  detail,
  isLoading,
  canResolve,
  onClose,
  onApprove,
  onDeny,
  isSubmitting,
}: Props) {
  const [decision, setDecision] = useState<"Approved" | "Denied">("Approved");
  const [directive, setDirective] = useState("");
  const [notes, setNotes] = useState("");
  const [certified, setCertified] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { dateTime } = useDateFormatter();

  useEffect(() => {
    if (!open) {
      setDecision("Approved");
      setDirective("");
      setNotes("");
      setCertified(false);
      setError(null);
    }
  }, [open]);

  if (!open) return null;
  const r = detail?.record ?? null;
  const alreadyResolved = r && r.status !== "Pending";

  const submit = async () => {
    setError(null);
    if (!directive.trim()) return setError("Resolution directive is required.");
    if (!notes.trim()) return setError("Resolution notes are required.");
    if (!certified) return setError("Please certify that you have reviewed this escalation.");
    try {
      if (decision === "Approved") {
        await onApprove({ resolutionNotes: notes, resolutionDirective: directive });
      } else {
        await onDeny({ resolutionNotes: notes, resolutionDirective: directive });
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to submit.");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end bg-black/30"
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-xl h-full bg-white-pure flex flex-col overflow-y-auto p-space-4">
        <header className="flex items-start justify-between gap-space-3">
          <div>
            <div className="text-caption text-gray-mid">Escalation</div>
            <h2 className="font-display text-h4 m-0">
              {detail?.checklistItemCode ?? "—"} {detail?.checklistItemTitle ?? ""}
            </h2>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="text-gray-mid">
            ✕
          </button>
        </header>

        {isLoading || !r ? (
          <div className="mt-space-4 text-gray-mid">Loading…</div>
        ) : (
          <>
            <section className="mt-space-3">
              <div className="text-caption text-gray-mid">Status</div>
              <div className="font-medium">{r.status}</div>
              <div className="text-caption text-gray-mid mt-space-2">Lane</div>
              <div>{r.lane}</div>
              <div className="text-caption text-gray-mid mt-space-2">Escalated by</div>
              <div>
                {r.escalatedByUserId} on {dateTime(r.escalatedAt)}
              </div>
              {detail?.caseNumber && (
                <>
                  <div className="text-caption text-gray-mid mt-space-2">Case</div>
                  <div className="font-mono">{detail.caseNumber}</div>
                </>
              )}
            </section>

            {detail?.checklistItemDescription && (
              <section className="mt-space-3">
                <div className="text-caption text-gray-mid">Item context</div>
                <div className="whitespace-pre-wrap">{detail.checklistItemDescription}</div>
                <a
                  className="text-gold underline"
                  href={`/reunderwriting/cases/${r.reunderwritingCaseId}`}
                >
                  Open in Case →
                </a>
              </section>
            )}

            <section className="mt-space-3">
              <div className="text-caption text-gray-mid">Analyst&apos;s escalation reason</div>
              <textarea
                value={r.escalationReason}
                readOnly
                rows={4}
                className="w-full border border-border-light rounded-md p-space-2"
              />
            </section>

            {alreadyResolved && (
              <section className="mt-space-3 p-space-3 bg-white-warm rounded-md">
                <div className="font-medium">
                  Resolved {r.status} by {r.resolvedByUserId ?? "—"}
                </div>
                <div className="text-caption text-gray-mid">
                  on {r.resolvedAt ? dateTime(r.resolvedAt) : "—"}
                </div>
                <div className="mt-space-2">
                  <div className="text-caption text-gray-mid">Directive</div>
                  <div>{r.resolutionDirective}</div>
                </div>
                <div className="mt-space-2">
                  <div className="text-caption text-gray-mid">Notes</div>
                  <div className="whitespace-pre-wrap">{r.resolutionNotes}</div>
                </div>
              </section>
            )}

            {!alreadyResolved && canResolve && (
              <section className="mt-space-3">
                <h3 className="font-display text-h5">Executive review</h3>
                <div className="flex flex-col gap-space-2">
                  <div className="flex gap-space-3">
                    <label className="flex items-center gap-space-1">
                      <input
                        type="radio"
                        checked={decision === "Approved"}
                        onChange={() => setDecision("Approved")}
                      />
                      Approve
                    </label>
                    <label className="flex items-center gap-space-1">
                      <input
                        type="radio"
                        checked={decision === "Denied"}
                        onChange={() => setDecision("Denied")}
                      />
                      Deny
                    </label>
                  </div>

                  <label className="text-caption text-gray-mid">Resolution directive</label>
                  <input
                    type="text"
                    value={directive}
                    onChange={(e) => setDirective(e.target.value)}
                    maxLength={200}
                    placeholder="e.g. Proceed with Waiver"
                    className="border border-border-light rounded-md px-space-2 py-space-1"
                  />
                  <div className="flex flex-wrap gap-space-1">
                    {DIRECTIVE_PRESETS.map((p) => (
                      <button
                        type="button"
                        key={p}
                        onClick={() => setDirective(p)}
                        className="text-caption px-space-2 py-space-1 border border-border-light rounded-md"
                      >
                        {p}
                      </button>
                    ))}
                  </div>

                  <label className="text-caption text-gray-mid mt-space-2">
                    Resolution notes
                  </label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={5}
                    className="w-full border border-border-light rounded-md p-space-2"
                  />

                  <label className="flex items-center gap-space-2 mt-space-2">
                    <input
                      type="checkbox"
                      checked={certified}
                      onChange={(e) => setCertified(e.target.checked)}
                    />
                    <span>I have reviewed this escalation.</span>
                  </label>

                  {error && (
                    <div role="alert" className="text-status-error">
                      {error}
                    </div>
                  )}

                  <div className="flex gap-space-2 mt-space-2">
                    <Button variant="primary" onClick={submit} disabled={isSubmitting}>
                      {decision === "Approved" ? "Approve escalation" : "Deny escalation"}
                    </Button>
                    <Button variant="tertiary" onClick={onClose}>
                      Cancel
                    </Button>
                  </div>
                </div>
              </section>
            )}

            {!alreadyResolved && !canResolve && (
              <section className="mt-space-3 p-space-3 bg-white-warm rounded-md text-gray-mid">
                You don&apos;t have permission to resolve escalations.
              </section>
            )}
          </>
        )}
      </div>
    </div>
  );
}
